import { computed, ref } from 'vue';

import { FREQUENCY_TYPE } from '~/constants';

import Service from '~/services/Service';

import type {
  IFormField,
  IFormFieldDate,
  IRecipientListForm,
  IRegistrationHighlightedOption,
  IStepperStep,
} from '~/types';

import { openFileInNewTab } from './fileUtilities';

export const getRecipientListDefaultSchema = (): Record<
  string,
  IFormField | IFormFieldDate
> => ({
  name: {
    name: 'Sharing_List_Name',
    label: 'Sharing list name',
    component: 'FieldText',
    rules: 'required:The list name is a mandatory field.|min:2|max:128',
    autocomplete: 'Sharing_List_Name',
    helpText: 'The list name will be visible to you only.',
  },
  message: {
    name: 'message',
    label: 'Message',
    placeholder: 'Add a message',
    component: 'FieldRichTextEditor',
    rules: 'max:512',
  },
  startRecurringDate: {
    name: 'start_date',
    label: 'Start date',
    component: 'FieldDate',
    minDate: new Date(),
    autocomplete: 'start_date',
    rules: 'required:Start date is a mandatory field',
  },
  frequency: {
    name: 'frequency',
    label: '',
    rules: 'required',
    options: [
      {
        id: FREQUENCY_TYPE.ONCE,
        label: 'Share once / Does not repeat',
      },
      {
        id: FREQUENCY_TYPE.WEEKLY,
        label: 'Weekly',
      },
      {
        id: FREQUENCY_TYPE.BI_WEEKLY,
        label: 'Every two weeks',
      },
      {
        id: FREQUENCY_TYPE.MONTHLY,
        label: 'Monthly',
      },
    ],
    component: 'FieldRadio',
  },
});

export const getRecipientListNewForm = (): IRecipientListForm => ({
  name: '',
  frequency: FREQUENCY_TYPE.ONCE,
  startRecurringDate: '',
  tradelanes: [],
  message: '',
  recipients: [],
  settings: {},
});

export const useRecipientListSteps = (
  form: IRecipientListForm,
  reportId?: number,
) => {
  const isPreviewLoading = ref(false);

  return computed<IStepperStep[]>(() => [
    {
      title: 'Settings',
      actionButtons: [
        {
          label: 'Cancel',
          type: 'action',
          variant: 'btn-tertiary',
          to: { name: 'AdminScheduleRecipientList' },
        },
      ],
    },
    {
      title: 'Recipients',
      actionButtons: [
        {
          label: 'Cancel',
          type: 'action',
          variant: 'btn-tertiary',
          to: { name: 'AdminScheduleRecipientList' },
        },
        {
          label: 'Preview',
          type: 'action',
          variant: 'btn-secondary',
          loading: isPreviewLoading.value,
          handler: () => {
            Service.recipientList()
              .emailPreview()
              .onStart(() => {
                isPreviewLoading.value = true;
              })
              .data({
                tradelanes: extractUniqueTradelaneIds(form.tradelanes),
                message: btoa(form.message),
                settings: form.settings,
                reportId,
              })
              .arrayFormat('indices')
              .onSuccess((data: string) => {
                openFileInNewTab({ file: data, type: 'text/html' });
              })
              .onFinish(() => {
                isPreviewLoading.value = false;
              })
              .execute();
          },
        },
        {
          label: 'Next',
          type: 'submit',
          disabled: !form.recipients.length,
        },
      ],
    },
    {
      title: 'Schedule',
      actionButtons: [
        {
          label: 'Cancel',
          type: 'action',
          variant: 'btn-tertiary',
          to: { name: 'AdminScheduleRecipientList' },
        },
        {
          label: 'Preview',
          type: 'action',
          variant: 'btn-secondary',
          loading: isPreviewLoading.value,
          handler: () => {
            Service.recipientList()
              .emailPreview()
              .onStart(() => {
                isPreviewLoading.value = true;
              })
              .data({
                tradelanes: extractUniqueTradelaneIds(form.tradelanes),
                message: btoa(form.message),
                settings: form.settings,
                reportId,
              })
              .arrayFormat('indices')
              .onSuccess((data: string) => {
                openFileInNewTab({ file: data, type: 'text/html' });
              })
              .onFinish(() => {
                isPreviewLoading.value = false;
              })
              .execute();
          },
        },
        {
          label: 'Schedule share',
          type: 'submit',
        },
      ],
    },
  ]);
};

export const extractUniqueTradelaneIds = (
  tradelanes: IRegistrationHighlightedOption[],
): string[] => {
  return [...new Set(tradelanes.flatMap((trade) => trade.inTradelanes))];
};
